import { useRef, useEffect } from 'react';
import type { VideoClip } from '../../types';

export interface PreviewPlayerProps {
    clip: VideoClip | null;
}

export function PreviewPlayer({ clip }: PreviewPlayerProps) {
    const videoRef = useRef<HTMLVideoElement>(null);
    const blobUrlRef = useRef<string | null>(null);

    // Update video source when clip changes
    useEffect(() => {
        const video = videoRef.current;
        if (!video || !clip) return;

        // Revoke previous blob URL to prevent memory leaks
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }

        // Create new blob URL
        const url = URL.createObjectURL(clip.file);
        blobUrlRef.current = url;
        video.src = url;
        video.currentTime = clip.startTime;

        // Cleanup on unmount or clip change
        return () => {
            if (blobUrlRef.current) {
                URL.revokeObjectURL(blobUrlRef.current);
                blobUrlRef.current = null;
            }
        };
    }, [clip]);

    return (
        <section className="bg-gray-800/50 backdrop-blur-lg border border-gray-700 rounded-xl overflow-hidden">
            <div className="aspect-video bg-black relative">
                {clip ? (
                    <video
                        ref={videoRef}
                        controls
                        className="w-full h-full"
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-center p-8">
                        <svg className="w-16 h-16 text-gray-600 mb-4" fill="none" viewBox="0 0 64 64">
                            <circle cx="32" cy="32" r="24" stroke="currentColor" strokeWidth="2" />
                            <path d="M26 22L42 32L26 42V22Z" fill="currentColor" />
                        </svg>
                        <p className="text-gray-400">Vælg et klip for at se preview</p>
                    </div>
                )}
            </div>

            {/* Video Info */}
            {clip && (
                <div className="p-4 border-t border-gray-700">
                    <p className="font-medium truncate mb-1">{clip.name}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span>{clip.metadata.width}x{clip.metadata.height}</span>
                        <span>•</span>
                        <span>{Math.round(clip.metadata.duration)}s</span>
                    </div>
                </div>
            )}
        </section>
    );
}
